/**
 * Admin · Overview — top-level director dashboard.
 *
 * 6 KPI cards + Status Overview bar chart + Attendance donut + Upcoming events
 * + Recent activity. Single endpoint feeds it all: GET /api/admin/workspaces/{id}/overview.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  LogIn,
  BedDouble,
  BadgeCheck,
  MessageCircle,
  Send,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { api } from "../../lib/api";
import { useAdminWorkspace, AdminHeader, AdminEmpty } from "./_shared";
import ActivityPulse from "../../components/ActivityPulse";

function KpiCard({ Icon, iconBg, iconFg, label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-3xl bg-white border hairline p-5 flex items-center gap-4"
    >
      <span
        className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0"
        style={{ background: iconBg }}
        aria-hidden
      >
        <Icon size={20} strokeWidth={1.7} style={{ color: iconFg }} />
      </span>
      <div className="min-w-0">
        <div
          data-testid={`${testid}-value`}
          className="font-serif text-3xl text-[#171717] leading-tight tabular-nums"
        >
          {value}
        </div>
        <div className="mt-1 text-[11px] tracking-[0.18em] uppercase text-[#525252] font-medium">
          {label}
        </div>
      </div>
    </div>
  );
}

const STATUS_COLOR = {
  safe: "#E7B7C8",
  arrived: "#E7B7C8",
  learning: "#9CC4A4",
  playing: "#F4D88F",
  eating: "#F4B68F",
  resting: "#9BC0E5",
};

const STATUS_LABEL = {
  safe: "In Care",
  arrived: "Arrived",
  learning: "In the Yard",
  playing: "At Play",
  eating: "Eating",
  resting: "Resting",
};

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return new Date(iso).toLocaleDateString();
}

export default function AdminOverview() {
  const { workspace, loading: wsLoading } = useAdminWorkspace();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspace) return;
    let alive = true;
    api
      .get(`/admin/workspaces/${workspace.id}/overview`)
      .then((r) => {
        if (alive) setData(r.data);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [workspace?.id]);

  const statusTotal = useMemo(() => {
    if (!data?.status_counts) return 0;
    return Object.values(data.status_counts).reduce((a, b) => a + b, 0) || 0;
  }, [data]);

  if (wsLoading) return null;
  if (!workspace)
    return (
      <div data-testid="admin-overview-no-ws">
        <AdminHeader
          eyebrow="Welcome"
          title="Let's set up your first center."
        />
        <section className="mt-8 rounded-3xl bg-white border hairline p-10 max-w-2xl">
          <p className="text-[15px] leading-relaxed text-[#525252]">
            Alouvya is built around a calm, real-time room. Create your first
            workspace and you'll get the Director Panel — Live status across
            every group, attendance, calm staff alerts, and a quiet timeline of
            everything that's happened today.
          </p>
          <p className="mt-4 text-[15px] leading-relaxed text-[#525252]">
            It only takes a moment. You can rename it later.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/workspace"
              data-testid="admin-overview-create-workspace"
              className="btn-primary"
            >
              Create a workspace
              <ArrowRight size={14} strokeWidth={1.6} />
            </Link>
            <Link
              to="/more"
              data-testid="admin-overview-explore"
              className="btn-ghost"
            >
              Explore settings first
            </Link>
          </div>
        </section>
      </div>
    );
  if (loading || !data)
    return (
      <AdminEmpty testid="admin-overview-loading">A quiet moment…</AdminEmpty>
    );

  return (
    <div data-testid="admin-overview-screen">
      <AdminHeader
        eyebrow={data.workspace_name || "Overview"}
        title={`Good day, ${(workspace.admins?.[0]?.name || "Director").split(" ")[0]}.`}
      />

      {/* KPI strip */}
      <section
        data-testid="admin-overview-kpis"
        className="mt-8 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3"
      >
        <KpiCard testid="kpi-total" label="Total children" value={data.total_children} Icon={Users} iconBg="var(--pastel-lavender)" iconFg="#1F3A2A" />
        <KpiCard testid="kpi-in" label="Checked in today" value={data.checked_in_today} Icon={LogIn} iconBg="var(--pastel-eat)" iconFg="#8A4A3B" />
        <KpiCard testid="kpi-absent" label="Absent today" value={data.absent_today} Icon={BedDouble} iconBg="var(--pastel-rest)" iconFg="#1B4A6B" />
        <KpiCard testid="kpi-staff" label="Staff on duty" value={data.staff_on_duty} Icon={BadgeCheck} iconBg="var(--pastel-play)" iconFg="#86601A" />
        <KpiCard testid="kpi-messages" label="Messages today" value={data.messages_sent_today} Icon={MessageCircle} iconBg="var(--pastel-pink)" iconFg="#BE185D" />
        <KpiCard testid="kpi-pickups" label="Pickup requests" value={data.pickup_requests_active} Icon={Send} iconBg="#F2EFEA" iconFg="#525252" />
      </section>

      {/* Activity Pulse — iter34 calm sparkline */}
      <section className="mt-10">
        <ActivityPulse workspaceId={workspace.id} testid="admin-activity-pulse" />
      </section>

      {/* Charts + activity */}
      <section className="mt-10 grid lg:grid-cols-3 gap-5">
        {/* Status overview bars */}
        <div
          data-testid="admin-status-overview"
          className="rounded-3xl bg-white border hairline p-6"
        >
          <span className="eyebrow">Status overview</span>
          <h3 className="mt-3 font-serif text-2xl tracking-tight">Where the room is now</h3>
          <ul className="mt-6 space-y-3" data-testid="admin-status-bars">
            {Object.entries(data.status_counts || {}).map(([key, count]) => {
              const pct = statusTotal > 0 ? (count / statusTotal) * 100 : 0;
              return (
                <li key={key} data-testid={`admin-status-${key}`}>
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="text-[#171717] font-medium">
                      {STATUS_LABEL[key] || key}
                    </span>
                    <span className="text-[#525252] tabular-nums">{count}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-[#F2EFEA] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${pct}%`,
                        background: STATUS_COLOR[key] || "#9BA995",
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Attendance donut */}
        <div
          data-testid="admin-attendance-donut"
          className="rounded-3xl bg-white border hairline p-6"
        >
          <span className="eyebrow">Attendance today</span>
          <h3 className="mt-3 font-serif text-2xl tracking-tight">A calm snapshot</h3>
          <AttendanceDonut
            total={data.total_children}
            present={data.checked_in_today}
            absent={data.absent_today}
            notYet={Math.max(
              0,
              (data.total_children || 0) -
                (data.checked_in_today || 0) -
                (data.absent_today || 0) -
                (data.departed_today || 0)
            )}
            departed={data.departed_today}
          />
        </div>

        {/* Upcoming events */}
        <div
          data-testid="admin-upcoming-events"
          className="rounded-3xl bg-white border hairline p-6"
        >
          <span className="eyebrow">Upcoming events</span>
          <h3 className="mt-3 font-serif text-2xl tracking-tight">Next 14 days</h3>
          {data.upcoming_events.length === 0 ? (
            <p
              className="mt-5 font-serif italic text-[#525252]"
              data-testid="admin-events-empty"
            >
              The calendar is gently open.
            </p>
          ) : (
            <ol
              data-testid="admin-events-list"
              className="mt-5 space-y-3"
            >
              {data.upcoming_events.map((e) => (
                <li
                  key={e.id}
                  data-testid={`admin-event-${e.id}`}
                  className="flex items-start gap-3"
                >
                  <span className="h-2 w-2 rounded-full bg-[#1F3A2A] mt-2 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-serif text-base text-[#171717] truncate">
                      {e.title}
                    </div>
                    <div className="text-xs text-[#525252] mt-0.5">
                      {new Date(e.starts_at).toLocaleString([], {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>

      {/* Recent activity */}
      <section
        data-testid="admin-recent-activity"
        className="mt-10 rounded-3xl bg-white border hairline p-6"
      >
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={14} strokeWidth={1.5} className="text-[#8A9984]" />
          <span className="eyebrow">Recent activity</span>
        </div>
        {data.recent_activity.length === 0 ? (
          <p
            className="font-serif italic text-[#525252]"
            data-testid="admin-activity-empty"
          >
            Nothing has stirred yet today.
          </p>
        ) : (
          <ol className="space-y-2" data-testid="admin-activity-list">
            {data.recent_activity.map((a, i) => (
              <li
                key={i}
                className="flex items-center gap-3 text-sm py-2 border-b last:border-b-0 border-[#171717]/4"
              >
                <span className="h-2 w-2 rounded-full bg-[#1F3A2A] shrink-0" />
                <span className="font-serif text-base text-[#171717] truncate flex-1 min-w-0">
                  {a.child_name}{" "}
                  <span className="text-[#525252] font-sans text-sm">
                    {a.subkind === "arrival" ? "signed in" : "signed out"} ·{" "}
                    {a.by_user_name}
                  </span>
                </span>
                <span className="text-[10px] tracking-[0.18em] uppercase text-[#525252]">
                  {timeAgo(a.at)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

/** Inline pure-CSS donut so we don't pull in a chart library. */
function AttendanceDonut({ total, present, absent, notYet, departed }) {
  const slices = [
    { key: "present", value: present || 0, color: "#9CC4A4", label: "Present" },
    { key: "absent", value: absent || 0, color: "#F4B68F", label: "Absent" },
    { key: "departed", value: departed || 0, color: "#D6CFE7", label: "Departed" },
    { key: "notYet", value: notYet || 0, color: "#E8E2D5", label: "Not yet" },
  ];
  const sum = slices.reduce((a, s) => a + s.value, 0) || 1;
  let acc = 0;
  const ringSegments = slices.map((s) => {
    const fromPct = (acc / sum) * 100;
    acc += s.value;
    const toPct = (acc / sum) * 100;
    return { ...s, fromPct, toPct };
  });
  const gradient = `conic-gradient(${ringSegments
    .map((s) => `${s.color} ${s.fromPct}% ${s.toPct}%`)
    .join(", ")})`;

  return (
    <div className="mt-6 flex items-center gap-6 flex-wrap">
      <div className="relative h-28 w-28 shrink-0">
        <div
          className="absolute inset-0 rounded-full"
          style={{ background: gradient }}
        />
        <div className="absolute inset-3 rounded-full bg-white flex items-center justify-center flex-col">
          <span
            className="font-serif text-2xl text-[#171717] tabular-nums"
            data-testid="admin-attendance-total"
          >
            {total ?? "—"}
          </span>
          <span className="text-[9px] tracking-[0.22em] uppercase text-[#525252]">
            children
          </span>
        </div>
      </div>
      <ul className="space-y-1.5 text-sm flex-1 min-w-[140px]">
        {slices.map((s) => (
          <li
            key={s.key}
            data-testid={`admin-attendance-legend-${s.key}`}
            className="flex items-center gap-2"
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{ background: s.color }}
            />
            <span className="text-[#171717] font-medium tabular-nums">
              {s.value}
            </span>
            <span className="text-[#525252]">{s.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
